import { supabase } from './supabase';

export type PaymentLedgerStatus =
  | 'pending'
  | 'completed'
  | 'failed'
  | 'refunded'
  | 'voided'
  | string;

export type PaymentLedgerTargetType =
  | 'invoice'
  | 'product_sale'
  | 'proposal'
  | 'gift_card'
  | 'subscription'
  | 'other';

export interface PaymentLedgerEntry {
  id: string;
  business_id: string;
  created_at: string;
  channel: string | null;
  method: string;
  status: PaymentLedgerStatus;
  payment_type: string | null;
  amount: number;
  base_amount: number;
  tip_amount: number;
  surcharge_amount: number;
  principal_amount: number;
  reference: string | null;
  notes: string | null;
  stripe_payment_intent_id: string | null;
  payout_id: string | null;
  invoice_id: string | null;
  booking_id: string | null;
  product_sale_id: string | null;
  proposal_id: string | null;
  gift_card_id: string | null;
  customer_name: string | null;
  customer_id: string | null;
  target_type: PaymentLedgerTargetType;
  target_number: string | null;
  // Stripe traceability
  stripe_checkout_session_id: string | null;
  balance_transaction_id: string | null;
  stripe_fee: number | null;
  stripe_net: number | null;
  payout_status: string | null;
  stripe_payout_id: string | null;
  payout_arrival_date: string | null;
  /** 'refund' rows come from refunds_ledger with a negative amount. */
  entry_kind?: 'payment' | 'refund';
  refund_destination?: 'card' | 'cash' | 'e_transfer' | null;
  refund_source?: string | null;
  refund_reason?: string | null;
  stripe_refund_id?: string | null;
}

/** Channel key a refund row shows under, by destination. */
const REFUND_CHANNEL: Record<string, string> = {
  card: 'card_refund',
  cash: 'cash',
  e_transfer: 'e_transfer',
};

const REFUND_SOURCE_LABEL: Record<string, string> = {
  deposit: 'Deposit refund',
  invoice_refund: 'Invoice refund',
  invoice_void: 'Voided invoice',
  wallet_payout: 'Wallet payout',
  goodwill: 'Goodwill refund',
  manual: 'Refund',
};

export function refundSourceLabel(source: string | null | undefined): string {
  return REFUND_SOURCE_LABEL[source || ''] || 'Refund';
}

/** True when the payment was processed through Stripe (has a PI or Stripe channel). */
export function isStripePayment(e: {
  stripe_payment_intent_id?: string | null;
  channel?: string | null;
}): boolean {
  if (e.stripe_payment_intent_id) return true;
  const ch = e.channel || '';
  return ch === 'card_online' || ch === 'card_tap_to_pay' || ch === 'card_reader' || ch === 'financing';
}

/** Stripe dashboard URL for a payment intent (business searches their own account). */
export function stripeDashboardUrl(paymentIntentId: string | null | undefined): string | null {
  if (!paymentIntentId) return null;
  return `https://dashboard.stripe.com/payments/${paymentIntentId}`;
}

const PAYOUT_STATUS_STYLES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  in_transit: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-200 text-gray-600',
};

export function getPayoutStatusStyle(status: string | null | undefined): string {
  return PAYOUT_STATUS_STYLES[status || ''] || 'bg-gray-100 text-gray-600';
}

export function formatPayoutStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Pending payout';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human labels for the normalized payment channel. */
export const CHANNEL_LABELS: Record<string, string> = {
  cash: 'Cash',
  debit: 'Debit',
  credit: 'Credit (recorded)',
  card_online: 'Card (Stripe)',
  card_tap_to_pay: 'Tap to Pay',
  card_reader: 'Card Reader',
  gift_card: 'Gift Card',
  wallet: 'Wallet',
  bank_transfer: 'Bank Transfer',
  check: 'Check',
  financing: 'Financing',
  other: 'Other',
  card_refund: 'Card refund',
  e_transfer: 'E-transfer',
};

export function getChannelLabel(channel: string | null | undefined): string {
  if (!channel) return 'Other';
  return CHANNEL_LABELS[channel] || channel;
}

export interface FetchPaymentsLedgerParams {
  start?: string | Date | null;
  end?: string | Date | null;
}

function toIso(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  return value instanceof Date ? value.toISOString() : value;
}

/**
 * Fetch the unified payments ledger for a business, optionally within a date
 * range. Money that left as refunds (card / cash / e-transfer) is merged in as
 * negative 'refund' rows, so totals and per-channel figures are net.
 */
export async function fetchPaymentsLedger(
  businessId: string,
  params: FetchPaymentsLedgerParams = {},
): Promise<PaymentLedgerEntry[]> {
  const start = toIso(params.start);
  const end = toIso(params.end);

  let payments = supabase
    .from('payments_ledger')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (start) payments = payments.gte('created_at', start);
  if (end) payments = payments.lte('created_at', end);

  let refunds = supabase
    .from('refunds_ledger')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (start) refunds = refunds.gte('created_at', start);
  if (end) refunds = refunds.lte('created_at', end);

  const [{ data, error }, { data: refundRows, error: refundError }] = await Promise.all([payments, refunds]);
  if (error) throw error;
  if (refundError) throw refundError;

  const refundEntries: PaymentLedgerEntry[] = ((refundRows || []) as any[]).map((r) => ({
    id: r.id,
    business_id: r.business_id,
    created_at: r.created_at,
    channel: REFUND_CHANNEL[r.destination] || 'other',
    method: r.destination,
    // Settled refunds count like completed payments (negative); card legs
    // still waiting on Stripe stay pending and are excluded from totals.
    status: r.status === 'succeeded' ? 'completed' : 'pending',
    payment_type: 'refund',
    amount: Number(r.amount) || 0,
    base_amount: Number(r.amount) || 0,
    tip_amount: 0,
    surcharge_amount: 0,
    principal_amount: Number(r.amount) || 0,
    reference: r.reference ?? null,
    notes: r.reason ?? null,
    stripe_payment_intent_id: null,
    payout_id: null,
    invoice_id: r.invoice_id ?? null,
    booking_id: r.booking_id ?? null,
    product_sale_id: null,
    proposal_id: null,
    gift_card_id: null,
    customer_name: r.customer_name || null,
    customer_id: r.customer_id ?? null,
    target_type: r.invoice_id ? 'invoice' : r.customer_id ? 'other' : 'other',
    target_number: r.target_number ?? null,
    stripe_checkout_session_id: null,
    balance_transaction_id: null,
    stripe_fee: null,
    stripe_net: null,
    payout_status: null,
    stripe_payout_id: null,
    payout_arrival_date: null,
    entry_kind: 'refund',
    refund_destination: r.destination,
    refund_source: r.source,
    refund_reason: r.reason ?? null,
    stripe_refund_id: r.stripe_refund_id ?? null,
  }));

  const merged = [...((data || []) as PaymentLedgerEntry[]).map(e => ({ ...e, entry_kind: 'payment' as const })), ...refundEntries];
  merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return merged;
}
